// Firebase auth for the tickets booking tool.
//
// ── Read this before "fixing" the project ──────────────────────────────────────
// This app authenticates against the WayQ *transit* Firebase project
// (wayq-transit-preprod / wayq-transit-prod). Transit's tenantauth resolves that
// issuer to the `wayq` tenant (migrations-transit/00003_platform_tenant.sql), so
// bookings made here live in a DIFFERENT tenant from real weebee customers and
// cannot see their data. That is deliberate — this is an internal booking tool.
//
// It used to sign in against the *weebee* project via a backend OTP + custom-token
// exchange. All of that is gone: there is no phone auth on the transit projects,
// and no weebee dependency of any kind remains.
//
// Google is the only sign-in provider enabled on these projects (see
// deploy/hosting/README.md) — the same choice webadmin makes.
import { initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { config } from './config';

const app = initializeApp({
  projectId: config.firebase.projectId,
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
});

export const auth = getAuth(app);

export async function signIn(): Promise<User> {
  await setPersistence(auth, browserLocalPersistence);
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const cred = await signInWithPopup(auth, provider);
  return cred.user;
}

/**
 * Operator sign-in with an email + password on the same transit Firebase project.
 *
 * Google is the primary provider, but its popup cannot be driven by an automated
 * browser, which makes the booking flow — wizard, payment, QR, cancel — untestable
 * end to end. Email/password is already enabled on these projects, so this exposes
 * an existing capability rather than widening the identity surface.
 *
 * There is deliberately NO sign-up here: accounts are provisioned out-of-band and
 * are useless until granted a role (scripts/set-admin-claim.sh), which is the
 * actual authorisation gate — see routes/_authed.tsx.
 */
export async function signInWithEmail(email: string, password: string): Promise<User> {
  await setPersistence(auth, browserLocalPersistence);
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  return cred.user;
}

export function signOutUser(): Promise<void> {
  return signOut(auth);
}

export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/** Custom-claim role from the current ID token ('' when absent). */
export async function getRole(): Promise<string> {
  const user = auth.currentUser;
  if (!user) return '';
  const result = await user.getIdTokenResult();
  return typeof result.claims['role'] === 'string' ? (result.claims['role'] as string) : '';
}

export function watchAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}
